import { Draggable, DraggableStateSnapshot, Droppable } from "@hello-pangea/dnd";
import { Chip, TableSortLabel } from "@material-ui/core";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import * as React from "react";
import Sticky from "react-sticky-el";
import styled from "styled-components";
import { t } from "../../i18n/util";
import { ApplicationStateEnum, GetApplicantsObject } from "../../network/APITypes";
import { THEME } from "../../theme/ThemeStore";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { ApplicantCard } from "./ApplicantCard";
import { SectionHeaderSmall } from "./Primitives";
import { APPROXIMATE_CARD_HEIGHT } from "../../config";

const LOAD_THRESHOLD = APPROXIMATE_CARD_HEIGHT * 2;

export type Column = {
    label: string;
    status: ApplicationStateEnum;
    items: GetApplicantsObject[];
    total: number;
    unreadApplicationsTotal?: number;
};

type Props = {
    column: Column;
    columnIndex: number;
    itemSourceColumnIndex: number | undefined;
    itemSourceItemIndex: number | undefined;
    setSourceItemIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
    setSourceColumnIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
    columnsOrderDir: ("asc" | "desc")[];
    setColumnsOrderDir: React.Dispatch<React.SetStateAction<("asc" | "desc")[]>>;
    switchColumnsChangeToggle: () => void;
    onLoadMore?: (() => void) | null;
};

const ColumnContainer = styled.div`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    flex-grow: 1;
    display: flex;
    overflow: auto;
    position: relative;
    flex-direction: column;
`;

export const KanbanColumn = observer(
    ({
        column,
        columnIndex,
        itemSourceColumnIndex,
        itemSourceItemIndex,
        setSourceItemIndex,
        setSourceColumnIndex,
        columnsOrderDir,
        setColumnsOrderDir,
        switchColumnsChangeToggle,
        onLoadMore
    }: Props) => {
        const isMobile = useMobileMediaQuery();

        // https://github.com/atlassian/react-beautiful-dnd/issues/374#issuecomment-569817782

        const getStyle = (style: React.CSSProperties, snapshot: DraggableStateSnapshot) => {
            if (!snapshot.isDragging)
                return {
                    marginBottom: 16
                };
            if (!snapshot.isDropAnimating) {
                return style;
            }

            return {
                ...style,
                // cannot be 0, but make it super tiny
                transitionDuration: `0.001s`
            };
        };

        const switchOrderDir = () => {
            let tmp: "asc" | "desc" = "asc";
            if (columnsOrderDir[columnIndex] === "asc") {
                tmp = "desc";
            }
            setColumnsOrderDir([
                ...columnsOrderDir.slice(0, columnIndex),
                tmp,
                ...columnsOrderDir.slice(columnIndex + 1)
            ]);
        };

        // react < 17 uses event pooling and thus trying to access event.target became unreliable
        const scrollTarget = React.useRef<HTMLDivElement | null>(null);

        const handleScroll = (e: React.TouchEvent<HTMLDivElement> | React.WheelEvent<HTMLDivElement>) => {
            if (!scrollTarget.current) {
                return;
            }

            const target = scrollTarget.current;
            const remainingScroll = target.scrollHeight - (target.offsetHeight + target.scrollTop);

            if (remainingScroll < LOAD_THRESHOLD && onLoadMore) {
                onLoadMore();
            }
        };

        // needed, otherwise eslint will complain about the debounce function
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const debounceHandleScroll = React.useCallback(debounce(handleScroll, 100), [handleScroll]);

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    width: "100%",
                    flexGrow: 1
                }}
            >
                <Sticky scrollElement="#stickyParent" stickyStyle={{ zIndex: 1 }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            padding: " 0 0 20px 14px",
                            maxHeight: isMobile ? 50 : 35,
                            flexGrow: 1,
                            gap: 12
                        }}
                        className="kanbanColumnHeader"
                    >
                        <SectionHeaderSmall label={column.label} />
                        {column.items && (
                            <Chip
                                size="small"
                                style={{ color: THEME.customColors.body1Dark, fontSize: 9, height: 17, marginTop: 4 }}
                                label={
                                    column.unreadApplicationsTotal
                                        ? `${column.unreadApplicationsTotal}/${column.total}`
                                        : column.total
                                }
                            />
                        )}
                        <TableSortLabel
                            active={true}
                            direction={columnsOrderDir[columnIndex]}
                            onClick={switchOrderDir}
                            style={{ marginTop: 4 }}
                        />
                    </div>
                </Sticky>
                <Droppable droppableId={columnIndex.toString()}>
                    {(provided, snapshot) => {
                        const highlight = snapshot.isDraggingOver && columnIndex >= (itemSourceColumnIndex ?? 0);

                        return (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                style={{
                                    backgroundColor: highlight ? THEME.customColors.primaryLight : "transparent",
                                    flexGrow: 1,
                                    transition: "background-color 200ms ease-in",
                                    padding: 14,
                                    borderRadius: "10px",
                                    display: "flex",
                                    flexDirection: "column"
                                }}
                            >
                                <ColumnContainer
                                    onWheelCapture={(event) => {
                                        if (event.deltaY && event.deltaY > 0) {
                                            debounceHandleScroll(event);
                                        }
                                    }}
                                    onTouchMove={(event) => {
                                        debounceHandleScroll(event);
                                    }}
                                    ref={scrollTarget}
                                >
                                    <div style={{ height: isMobile ? "unset" : 0, flexGrow: 1, paddingTop: 4 }}>
                                        {column.items?.map((item, itemIndex) => (
                                            <React.Fragment key={item.id}>
                                                <Draggable key={item.id} draggableId={item.id} index={itemIndex}>
                                                    {(provided, snapshot) => {
                                                        return (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                style={getStyle(
                                                                    {
                                                                        marginBottom: 16,
                                                                        ...provided.draggableProps.style
                                                                    },
                                                                    snapshot
                                                                )}
                                                                onMouseDown={
                                                                    isMobile
                                                                        ? undefined
                                                                        : () => {
                                                                              setSourceItemIndex(itemIndex);
                                                                              setSourceColumnIndex(columnIndex);
                                                                          }
                                                                }
                                                                onTouchStart={
                                                                    isMobile
                                                                        ? () => {
                                                                              setSourceItemIndex(itemIndex);
                                                                              setSourceColumnIndex(columnIndex);
                                                                          }
                                                                        : undefined
                                                                }
                                                            >
                                                                <ApplicantCard
                                                                    item={item}
                                                                    switchColumnsChangeToggle={
                                                                        switchColumnsChangeToggle
                                                                    }
                                                                />
                                                            </div>
                                                        );
                                                    }}
                                                </Draggable>
                                                {columnIndex === itemSourceColumnIndex &&
                                                    itemIndex === itemSourceItemIndex &&
                                                    provided.placeholder}
                                            </React.Fragment>
                                        ))}
                                        {onLoadMore && (
                                            <div
                                                onClick={onLoadMore}
                                                style={{
                                                    cursor: "pointer",
                                                    color: THEME.customColors.headingsDark
                                                }}
                                                className="body2semibold"
                                            >
                                                {t("screen.dashboard.showMore")}
                                            </div>
                                        )}
                                        <div style={{ height: 1 }} />
                                    </div>
                                </ColumnContainer>
                                {provided.placeholder}
                            </div>
                        );
                    }}
                </Droppable>
            </div>
        );
    }
);
